import { useQuery } from '@tanstack/react-query';
import { toReadableValue } from '@ui/lib/bignumber';
import BigNumber from 'bignumber.js';

import { getPythNetworkUrl } from '../pyth-price-update/pythNetworkGets';

export const usePythHermesPriceAt = ({
  pythIds,
  timestampSecond,
  enabled,
}: {
  pythIds: string[];
  timestampSecond: number;
  enabled?: boolean;
}) => {
  return useQuery({
    enabled,
    // static data, prevent auto refresh
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryKey: usePythHermesPriceAt.getKey(pythIds, timestampSecond),
    queryFn: usePythHermesPriceAt.getFn(pythIds, timestampSecond),
  });
};

type Response = {
  parsed: [
    {
      id: string;
      price: {
        expo: number; //decimals precision, e.g. -8
        price: string;
      };
    },
  ];
};
type ParsedResponse = {
  id: string;
  price: BigNumber;
};
const HERMES_BATCH_SIZE = 20;

const mapParsedResponse = (json: Response): ParsedResponse[] => {
  return (json.parsed ?? []).map((price) => {
    return {
      id: price.id,
      price: toReadableValue(BigInt(price.price.price), -price.price.expo),
    };
  });
};

const requestHermesBatch = async ({
  pythIds,
  timestampSecond,
}: {
  pythIds: string[];
  timestampSecond: number;
}): Promise<ParsedResponse[]> => {
  if (pythIds.length === 0) {
    return [];
  }

  const res = await fetch(
    `${getPythNetworkUrl()}/v2/updates/price/${timestampSecond}?${pythIds
      .map((id) => `ids%5B%5D=${id}`)
      .join('&')}&encoding=base64&parsed=true`,
  );
  if (!res.ok) {
    throw new Error(`Hermes request failed with status ${res.status}`);
  }

  const json = (await res.json()) as Response;

  return mapParsedResponse(json);
};

const requestHermesBatchWithSplitFallback = async ({
  pythIds,
  timestampSecond,
}: {
  pythIds: string[];
  timestampSecond: number;
}): Promise<ParsedResponse[]> => {
  try {
    return await requestHermesBatch({ pythIds, timestampSecond });
  } catch {
    // Some ids (e.g. closed-market assets at that timestamp) can fail the whole batch.
    // Split recursively so valid ids still return data.
    if (pythIds.length <= 1) {
      return [];
    }

    const mid = Math.floor(pythIds.length / 2);
    const left = await requestHermesBatchWithSplitFallback({
      pythIds: pythIds.slice(0, mid),
      timestampSecond,
    });
    const right = await requestHermesBatchWithSplitFallback({
      pythIds: pythIds.slice(mid),
      timestampSecond,
    });

    return [...left, ...right];
  }
};

usePythHermesPriceAt.getKey = (pythIds: string[], timestampSecond: number) => [
  'pyth-hermes-updates-price',
  { pythIds, timestampSecond },
];
usePythHermesPriceAt.getFn =
  (pythIds: string[], timestampSecond: number) =>
  async (): Promise<ParsedResponse[]> => {
    try {
      const merged: ParsedResponse[] = [];
      for (let i = 0; i < pythIds.length; i += HERMES_BATCH_SIZE) {
        const chunk = pythIds.slice(i, i + HERMES_BATCH_SIZE);
        const partial = await requestHermesBatchWithSplitFallback({
          pythIds: chunk,
          timestampSecond,
        });
        merged.push(...partial);
      }

      return merged;
    } catch {
      return [];
    }
  };
